import flow from 'lodash/flow';
import map from 'lodash/map';
import compact from 'lodash/compact';
import values from 'lodash/values';
import pickBy from 'lodash/pickBy';
import get from 'lodash/get';
import moment from '../modules/moment';
import Vue from 'vue';

/**
 * Return the price in a basic currency format
 * @example 7.3 -> 7.30
 * @example 1337.3 -> 1,337.30
 * @example 1337.00 -> 1,337
 *
 * @param {Number} val
 * @param {Boolean} trim
 *
 * @returns {String}
 */
export function currencyFormat(val, trim = false) {
    if (!val || isNaN(val)) {
        val = 0;
    }

    return parseFloat(val)
        .toFixed(trim ? 0 : 2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Join an array of items into a single delimited string
 *
 * @param {array} val
 * @param {string} delimiter
 *
 * @returns {*}
 */
export function join(val, delimiter = ',') {
    val = (flow(
        map((v) => v),
        compact,
        values
    )(val)).join(delimiter);

    return val;
}

/**
 * Uppercase the first character of a string
 *
 * @param {string} val
 *
 * @returns {string}
 */
export function ucfirst(val) {
    return val.charAt(0).toUpperCase() + val.slice(1);
}

/**
 * Return an extremely basic pluralisation of a word
 *
 * @param {String} word
 * @param {Number} count
 * @param {String} pluralText
 *
 * @returns {String}
 */
export function plural(word, count = 0, pluralText) {
    if (count === 1) {
        return word;
    }

    return pluralText || `${word}s`;
}

/**
 * Takes in an float of hours and returns a formatted string
 *
 * @param {String} hours
 * @param {Boolean=true} showDays
 *
 * @returns {String}
 */
export function formattedHours(hours, showDays = true) {
    let days = 0;
    let returnString = '';

    hours = parseFloat(hours);

    if (hours > 24 && showDays) {
        days = Math.floor(hours / 24);
        hours = hours - (days * 24);

        returnString = `${days} ${plural('day', days)} `;

        if (hours === 0) {
            return returnString;
        }
    }

    return `${returnString}${hours.toFixed(1).replace('.0', '')} ${plural('hour', hours)}`;
}

/**
 * Format the given date if possible
 *
 * @param {string} date
 * @param {string} newFormat
 * @param {string} originalFormat
 *
 * @returns {string}
 */
export function formatDate(date, newFormat = 'DD/MM/YYYY', originalFormat = undefined) {
    const newDate = moment(date, originalFormat);

    if (!newDate.isValid()) {
        return newDate;
    }

    return newDate.format(newFormat);
}

/**
 * Return the full name of a user
 *
 * @param user
 *
 * @returns {*}
 */
export function fullName(user) {
    return pickBy(user, ['first_name', 'last_name']).join(' ');
}

/**
 * Return the full name of a user
 *
 * @param {Object} object
 * @param {String} path
 * @param {*} fallback
 *
 * @returns {*}
 */
export function optional(object, path, fallback = null) {
    return get(object, path, fallback);
}

/**
 * Generate a human readable time diff
 *
 * @param {String} date
 *
 * @return {String}
 */
export function diffForHumans(date) {
    return new moment(date).fromNow();
}

/**
 * Return the season plan in a human-readable format.
 * @example mon-sun -> Mon - Sun
 * @example any-three-days -> Any 3 days
 *
 * @param {String} plan
 *
 * @return {String}
 */
export function seasonPlanFormatter(plan) {
    const plans = [
        { value: 'mon-sun', text: 'Mon - Sun'},
        { value: 'mon-fri', text: 'Mon - Fri only'},
        { value: 'any-three-days', text: 'Any 3 days'},
    ];

    return plans.find(({ value }) => plan === value).text
}

/**
 * Autoload any filters by default, will be available on
 * the whole Vue instance
 */
export const defaultFilters = {
    currencyFormat,
    join,
    ucfirst,
    plural,
    formatDate,
    formattedHours,
    optional,
    diffForHumans,
    seasonPlanFormatter,
};

/**
 * Load global Vue Filters
 * usage: {{ value | filterName }} e.g. {{ price | currencyFormat }}
 *
 * @param {Object} filters
 */
export const loader = (filters = defaultFilters) => {
    Object.keys(filters).forEach((filter) => {
        Vue.filter(filter, filters[filter]);
    });
};
